/**
 * In-page links on the public website glide to their target instead of
 * jumping, so "See how it works" travels through the hero's scroll scene the
 * way the reference does. The pure parts (duration, easing, target position,
 * header offset, the sticky header's scroll states) live at the top and are
 * unit tested; the DOM-driving functions below them are the only place that
 * touches `window`.
 *
 * Attach once per page root with `attachAnchorGlide` (SiteShell does this
 * from a ref callback). Every `a[href^="#"]` click inside that root then
 * glides: 1400 to 2400 ms scaled by distance, cubic in-out, re-targeted every
 * frame because the hero changes height under the scroll, offset by the
 * header height (plus 28px above the timeline track), instant under reduced
 * motion, and cancelled the moment the visitor scrolls themselves.
 */

export const GLIDE_MIN_MS = 1400
export const GLIDE_MAX_MS = 2400
/** Milliseconds of travel per pixel of distance, before clamping. */
export const GLIDE_MS_PER_PX = 1.4
/** Extra room above the timeline track so its heading clears the header. */
export const TRACK_EXTRA_OFFSET = 28
/** Scrolling this little is not worth animating. */
const MIN_GLIDE_DISTANCE = 2
/** The bar grows its hairline shadow once the page has scrolled this far. */
export const SCROLLED_AFTER = 10
/** The header is "on the photo" while the hero's bottom is below this share of the viewport. */
export const ON_PHOTO_VIEWPORT_SHARE = 0.6

/** How long a glide over `distancePx` takes: 1.4 ms per px, clamped to 1.4 to 2.4 s. */
export function glideDuration(distancePx: number): number {
	const scaled = Math.abs(distancePx) * GLIDE_MS_PER_PX
	return Math.min(GLIDE_MAX_MS, Math.max(GLIDE_MIN_MS, scaled))
}

/** Cubic ease in-out on 0..1 (clamped). */
export function easeInOutCubic(t: number): number {
	const k = Math.min(1, Math.max(0, t))
	return k < 0.5 ? 4 * k * k * k : 1 - (-2 * k + 2) ** 3 / 2
}

/** Where the window must scroll so `rectTop` lands `offset` px below the top. */
export function targetScrollTop({
	rectTop,
	scrollY,
	offset,
	maxScroll = Number.POSITIVE_INFINITY,
}: {
	rectTop: number
	scrollY: number
	offset: number
	maxScroll?: number
}): number {
	const wanted = rectTop + scrollY - offset
	return Math.max(0, Math.min(Math.max(0, maxScroll), wanted))
}

/** The scroll position `progress` (0..1) of the way from `from` to `to`. */
export function glidePosition(from: number, to: number, progress: number) {
	return from + (to - from) * easeInOutCubic(progress)
}

/** How far below the viewport top a glide target settles. */
export function glideOffset({
	headerHeight,
	targetId,
	trackId,
}: {
	headerHeight: number
	targetId: string
	trackId: string | null
}): number {
	return (
		headerHeight +
		(trackId !== null && targetId === trackId ? TRACK_EXTRA_OFFSET : 0)
	)
}

/** "#how" gives "how"; "#", "", "/x#how" and undecodable hashes give null. */
export function anchorIdFromHref(
	href: string | null | undefined,
): string | null {
	if (!href || href.length < 2 || !href.startsWith('#')) return null
	try {
		const id = decodeURIComponent(href.slice(1))
		return id.length > 0 ? id : null
	} catch {
		return null
	}
}

/**
 * The section a link on another page asked the next page to glide to,
 * carried as `{ glideTo: "how" }` in React Router navigation state.
 */
export function glideTargetFromState(state: unknown): string | null {
	if (typeof state !== 'object' || state === null) return null
	const target = (state as { glideTo?: unknown }).glideTo
	return typeof target === 'string' && target.length > 0 ? target : null
}

/** A primary-button click with no modifier, the kind a link should answer. */
export function isPlainLeftClick(event: {
	button: number
	metaKey: boolean
	ctrlKey: boolean
	shiftKey: boolean
	altKey: boolean
	defaultPrevented: boolean
}): boolean {
	return (
		!event.defaultPrevented &&
		event.button === 0 &&
		!event.metaKey &&
		!event.ctrlKey &&
		!event.shiftKey &&
		!event.altKey
	)
}

/** Elements that take focus without a tabindex (links only with an href). */
const NATIVELY_FOCUSABLE = new Set([
	'BUTTON',
	'INPUT',
	'SELECT',
	'TEXTAREA',
	'SUMMARY',
	'IFRAME',
])

/**
 * Whether an in-page link's target needs a temporary `tabindex="-1"` before
 * it can take focus: true for headings, sections and other plain elements,
 * false for anything already focusable or carrying its own tabindex.
 */
export function needsLandingTabIndex(el: {
	tagName: string
	hasAttribute: (name: string) => boolean
}): boolean {
	if (el.hasAttribute('tabindex')) return false
	const tag = el.tagName.toUpperCase()
	if (tag === 'A' || tag === 'AREA') return !el.hasAttribute('href')
	return !NATIVELY_FOCUSABLE.has(tag)
}

/**
 * The sticky header's two scroll states. `scrolled` draws the hairline
 * shadow; `onPhoto` keeps the soft white gradient under the bar while the
 * photo hero is still behind it (its bottom below 60% of the viewport, as the
 * reference decides it). Without a measured hero the gradient follows the
 * scroll position as if the hero were one viewport tall.
 */
export function headerScrollState({
	scrollY,
	hasPhotoHero,
	heroBottom,
	viewportHeight,
}: {
	scrollY: number
	hasPhotoHero: boolean
	heroBottom: number | null
	viewportHeight: number
}): { scrolled: boolean; onPhoto: boolean } {
	const scrolled = scrollY > SCROLLED_AFTER
	if (!hasPhotoHero) return { scrolled, onPhoto: false }
	const threshold = viewportHeight * ON_PHOTO_VIEWPORT_SHARE
	const bottom = heroBottom ?? viewportHeight - scrollY
	return { scrolled, onPhoto: bottom > threshold }
}

/* ----------------------------------------------------------------------- */
/* DOM                                                                     */
/* ----------------------------------------------------------------------- */

const SCROLL_KEYS = new Set([
	'ArrowUp',
	'ArrowDown',
	'PageUp',
	'PageDown',
	'Home',
	'End',
	' ',
])

function prefersReducedMotion() {
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function maxScrollTop() {
	return document.documentElement.scrollHeight - window.innerHeight
}

let activeGlide: (() => void) | null = null

/**
 * Move keyboard and screen-reader focus to where an in-page link landed, so
 * the next Tab continues from the section and not from the link (or from
 * <body> when the link has gone, as in a folded list). It never scrolls: the
 * glide owns the scroll. A plain element gets `tabindex="-1"` and no focus
 * outline for as long as it holds focus (it is a landing point, not a
 * control); both are removed again on blur.
 */
export function focusLandingTarget(el: HTMLElement) {
	if (needsLandingTabIndex(el)) {
		const outline = el.style.outline
		el.setAttribute('tabindex', '-1')
		el.style.outline = 'none'
		el.addEventListener(
			'blur',
			() => {
				el.removeAttribute('tabindex')
				el.style.outline = outline
			},
			{ once: true },
		)
	}
	el.focus({ preventScroll: true })
}

/**
 * Glide the window so `el` sits `offset` px below the viewport top. Returns a
 * cancel function. Only one glide runs at a time; a wheel, touch or scroll
 * key from the visitor cancels it so the page never fights them.
 */
export function glideToElement(
	el: Element,
	{ offset, instant = false }: { offset: number; instant?: boolean },
): () => void {
	activeGlide?.()
	const target = () =>
		targetScrollTop({
			rectTop: el.getBoundingClientRect().top,
			scrollY: window.scrollY,
			offset,
			maxScroll: maxScrollTop(),
		})
	const y0 = window.scrollY
	const dy = target() - y0
	if (Math.abs(dy) < MIN_GLIDE_DISTANCE) return () => {}
	if (instant || prefersReducedMotion()) {
		window.scrollTo({ top: target(), behavior: 'instant' })
		return () => {}
	}

	const duration = glideDuration(dy)
	let frame = 0
	let settle = 0
	let t0: number | null = null
	let done = false
	const stop = () => {
		if (done) return
		done = true
		cancelAnimationFrame(frame)
		window.clearTimeout(settle)
		window.removeEventListener('wheel', stop)
		window.removeEventListener('touchstart', stop)
		window.removeEventListener('keydown', stopOnScrollKey)
		if (activeGlide === stop) activeGlide = null
	}
	const stopOnScrollKey = (event: KeyboardEvent) => {
		if (SCROLL_KEYS.has(event.key)) stop()
	}
	window.addEventListener('wheel', stop, { passive: true })
	window.addEventListener('touchstart', stop, { passive: true })
	window.addEventListener('keydown', stopOnScrollKey)

	const step = (now: number) => {
		if (done) return
		if (t0 === null) t0 = now
		const k = Math.min(1, (now - t0) / duration)
		// One layout read (the target rect), then one write.
		window.scrollTo({
			top: glidePosition(y0, target(), k),
			behavior: 'instant',
		})
		if (k < 1) {
			frame = requestAnimationFrame(step)
			return
		}
		// The hero's sticky scene may still be settling: land once more.
		settle = window.setTimeout(() => {
			if (!done) window.scrollTo({ top: target(), behavior: 'instant' })
			stop()
		}, 120)
	}
	frame = requestAnimationFrame(step)
	activeGlide = stop
	return stop
}

export interface AnchorGlideOptions {
	/** Selector for the sticky header inside the root; its height is the offset. */
	headerSelector: string
	/** The timeline track's id: it gets 28px more room above it. */
	trackId: string | null
}

function headerHeight(root: HTMLElement, selector: string) {
	const header = root.querySelector<HTMLElement>(selector)
	return header ? header.offsetHeight : 0
}

function isRendered(el: Element) {
	return el.getClientRects().length > 0
}

/**
 * Click delegation for every `a[href^="#"]` inside `root`. Focus moves to the
 * target at once (without scrolling) and the window glides there. Links
 * whose target is missing or not rendered keep their default behaviour. Returns a detach
 * function (a React 19 ref callback can return it as its cleanup).
 */
export function attachAnchorGlide(
	root: HTMLElement,
	options: AnchorGlideOptions,
): () => void {
	const onClick = (event: MouseEvent) => {
		if (!isPlainLeftClick(event)) return
		const anchor = (event.target as Element | null)?.closest?.('a[href^="#"]')
		if (!(anchor instanceof HTMLAnchorElement) || !root.contains(anchor)) return
		const id = anchorIdFromHref(anchor.getAttribute('href'))
		if (!id) return
		const el = document.getElementById(id)
		if (!el || !isRendered(el)) return
		event.preventDefault()
		focusLandingTarget(el)
		glideToElement(el, {
			offset: glideOffset({
				headerHeight: headerHeight(root, options.headerSelector),
				targetId: id,
				trackId: options.trackId,
			}),
		})
	}
	root.addEventListener('click', onClick)
	return () => root.removeEventListener('click', onClick)
}

/**
 * Land on an in-page target right after a page mounts. Two cases:
 *
 * - `glideTo` (from navigation state): a link on another page asked for a
 *   section, for example the header's "How it Works" on /terms. React Router
 *   scrolls the new page to the top in its layout effect, which runs after
 *   this ref callback, so the glide starts a frame later and travels from
 *   the top through the hero.
 * - The URL carries a hash (a pasted `/#how`): the browser or React Router
 *   has already jumped to the element, so only the header offset is
 *   corrected, instantly.
 *
 * Either way focus moves to the target, as a click on an in-page link does.
 *
 * Returns a cancel function for the pending frame.
 */
export function landOnMount(
	root: HTMLElement,
	options: AnchorGlideOptions & { glideTo: string | null },
): () => void {
	const hashId = anchorIdFromHref(window.location.hash)
	const id = options.glideTo ?? hashId
	if (!id) return () => {}
	const el = document.getElementById(id)
	if (!el || !isRendered(el)) return () => {}
	let cancel: (() => void) | null = null
	const frame = requestAnimationFrame(() => {
		focusLandingTarget(el)
		cancel = glideToElement(el, {
			offset: glideOffset({
				headerHeight: headerHeight(root, options.headerSelector),
				targetId: id,
				trackId: options.trackId,
			}),
			instant: options.glideTo === null,
		})
	})
	return () => {
		cancelAnimationFrame(frame)
		cancel?.()
	}
}
